import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { getIdvDetailedJson } from 'api/file/file-api';
import { DocType, IdentityVerificationResult, Language } from 'api/file/interfaces/get-file-order-insights';
import { getAdhocOrderJson, getMarketplaceOrderReport } from 'api/orderInsights/order-insights-api';
import { ASC_STAFF } from 'utilities/constants/activity-rights';
import { DocumentType, newWindow } from 'features/constants';
import {
  docTypeValue,
  identityReportAvailableText,
  languageValue,
  oldVerificationRequestText,
} from 'features/help/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import PDFIcon from 'ui-kit/icons/PDF';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { formatDate, getVerificationExpiryDate, isPastExpiry } from 'utilities/dateFormatter';
import { formatPhoneNumber, openDocument } from 'utilities/utilities';
import IdvDetailedJsonDialog from './IdvDetailedJsonDialog';
import OrderInsightAlert from './OrderInsightAlerts';
import StatusRow from './StatusRow';
import FileDownloadOutlineIcon from 'ui-kit/icons/FileDownloadOutline';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  requestInfoContainer: {
    marginBottom: theme.spacing(2),
  },
  riskTypography: {
    '& a': {
      color: '#5F2120',
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  buttonSpacing: {
    marginBottom: theme.spacing(2),
  },
  heading: {
    paddingLeft: theme.spacing(2),
    fontWeight: 600,
  },
  pdfHeader: {
    letterSpacing: pxToRem(0.5),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  reportButton: {
    color: '#014361',
    marginTop: `-${theme.spacing(1)}px`,
    '& .MuiAgentNetButton-Spinner': {
      marginRight: theme.spacing(1),
    },
  },
}));

const INTELLICHECK_STATUS = {
  IDLE: 'idle',
  PENDING: 'pending',
  SUCCESS: 'success',
  ERROR: 'error',
} as const;

interface VerificationResultsProps extends IdentityVerificationResult {
  fileId?: string;
  index?: number;
  timezone?: string;
  showRequestedDataOnly?: boolean;
  qaPrefix?: string;
  isPartyVerification?: boolean;
  isDrawer?: boolean;
}

interface StatusItem {
  tooltipText?: string;
  label: string;
  value: string | undefined;
  noStatus?: boolean;
}

const VerificationResults: FC<VerificationResultsProps> = ({
  transactionId = '',
  summary,
  submittedData,
  isPdf = false,
  fileId,
  index = 0,
  timezone,
  showRequestedDataOnly = false,
  qaPrefix = '',
  isPartyVerification = false,
  isDrawer = false,
}) => {
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { fileData } = fileDataCtx;
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile } = profileCtx;
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const showNameMatch = useFeatureFlag(FeatureFlag.SHOW_IDV_NAME_MATCH, false);

  const showDetailedJson = profile?.activityRights?.some((rights) => rights.ActivityRightId === ASC_STAFF);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isInteractive = !isPdf;

  const moreInfoTestId = useMemo(() => {
    if (!isInteractive) {
      return undefined;
    }
    if (isPartyVerification && isDrawer) {
      return 'ad-hoc-identity-verification-button-more-info';
    }
    return `identity-verification-button-more-info-${index}`;
  }, [isInteractive, isPartyVerification, isDrawer, index]);

  const drawerIdentityVerificationReportTestId = useMemo(() => {
    if (isPartyVerification && isDrawer) {
      return 'ad-hoc-identity-verification-button-identity-verification-report';
    }
    return `${qaPrefix}-button-identity-verification-report`;
  }, [isPartyVerification, isDrawer, qaPrefix]);

  // Extract summary data with safe defaults
  const {
    customerName,
    transactionDate = '',
    barcodeRecognized,
    expiredId,
    ocrBarcodeMatches,
    documentLivenessFront,
    selfie,
    deviceAddress,
    phoneNumber,
    risks = [],
    formatRecognized,
    selfieLive,
    selfieMatch,
    nameMatchResult,
    nameMatchReason,
  } = summary ?? {};

  const {
    language = Language.ENGLISH,
    docType = DocType.NORTH_AMERICAN_DRIVER_LICENSE,
    phoneNumber: submittedPhoneNumber = '',
    emailAddress = '',
    verifyWithEmail = false,
  } = submittedData ?? {};

  const requestInfo = useMemo(
    () => [
      ...(verifyWithEmail
        ? [{ label: 'Email:', value: emailAddress }]
        : [{ label: 'Phone Number:', value: formatPhoneNumber(submittedPhoneNumber) }]),
      { label: 'Document Type:', value: docTypeValue[docType] },
      { label: 'Language:', value: languageValue[language] },
    ],
    [submittedPhoneNumber, docType, language, emailAddress, verifyWithEmail],
  );

  const requestInfoContainer: ReactNode = (
    <div className={classes.requestInfoContainer}>
      <Typography variant="h4" className={`${classes.heading} ${isPdf ? classes.pdfHeader : ''}`}>
        Request Info
      </Typography>
      <Box>
        {requestInfo.map((item, idx) => (
          <StatusRow
            key={`${item.label}-${idx}`}
            index={idx}
            label={item.label}
            isPdf={isPdf}
            value={item.value}
            isPlainText
          />
        ))}
      </Box>
    </div>
  );

  // Memoized status configurations
  const statuses: StatusItem[] = useMemo(() => {
    const isNADL = docType === DocType.NORTH_AMERICAN_DRIVER_LICENSE;
    return [
      { label: 'Name on ID:', value: customerName, noStatus: true },
      {
        label: 'Transaction Date:',
        value: formatDate(transactionDate, undefined, 'full', '', timezone),
        noStatus: true,
      },
      ...(isPdf || (showNameMatch?.enabled && showNameMatch?.ready)
        ? [
            {
              label: 'Name on ID Matches Party:',
              value: nameMatchResult,
              tooltipText: nameMatchReason,
            },
          ]
        : []),
      {
        label: isNADL ? 'Back of ID Barcode Recognized:' : '	Format Recognized:',
        value: isNADL ? barcodeRecognized : formatRecognized,
      },
      { label: 'Expired ID:', value: expiredId },
      {
        label: isNADL ? 'Printed Data on ID Matches Barcode Data:' : 'OCR-MRZ Match:',
        value: ocrBarcodeMatches,
      },
      { label: 'Live ID Document:', value: documentLivenessFront },
      ...(selfieLive && selfieMatch
        ? [
            { label: 'Live Selfie:', value: selfieLive },
            { label: 'Selfie Matches:', value: selfieMatch },
          ]
        : [{ label: 'Selfie:', value: selfie }]),
      { label: 'Device Address:', value: deviceAddress },
      { label: 'Phone Number:', value: phoneNumber },
    ];
  }, [
    customerName,
    formatRecognized,
    transactionDate,
    barcodeRecognized,
    expiredId,
    ocrBarcodeMatches,
    documentLivenessFront,
    selfie,
    selfieLive,
    selfieMatch,
    deviceAddress,
    phoneNumber,
    docType,
    isPdf,
    showNameMatch,
    nameMatchResult,
    nameMatchReason,
  ]);

  // Computed values for report availability
  const expiryDateFormatted = useMemo(() => getVerificationExpiryDate(transactionDate), [transactionDate]);
  const isOlderThan90Days = useMemo(() => isPastExpiry(transactionDate), [transactionDate]);
  const isReportAvailable = useMemo(() => !isOlderThan90Days, [isOlderThan90Days]);
  const identityReportMessage = useMemo(() => {
    return isReportAvailable ? identityReportAvailableText.replace('expiryDate', expiryDateFormatted) : '';
  }, [isReportAvailable, expiryDateFormatted]);

  // Report generation handler
  const handleOpenVerificationReport = useCallback(async () => {
    if (!fileId || !transactionId) {
      throw new Error('Missing required parameters for report generation');
    }
    const token = await getAccessToken();

    try {
      const pdfResponse = await getMarketplaceOrderReport(fileId, transactionId, token, isPartyVerification);
      openDocument(pdfResponse.Result.Data, DocumentType, newWindow, '');
    } catch (error) {
      if (!isPartyVerification) {
        addGlobalMsg({
          message: 'Error Downloading IntelliCheck Report.',
          type: 'error',
        });
      }
    }
  }, [fileId, transactionId, fileData?.fileNumber, getAccessToken, addGlobalMsg, isPartyVerification]);

  const { execute: executeIntelliCheckReport, status: intelliCheckReportStatus } = useAsync(
    handleOpenVerificationReport,
    false,
  );

  const handleViewDetailedJson = useCallback(async () => {
    if (!fileId || !transactionId) return;
    setJsonLoading(true);
    setJsonError(null);
    setJsonDialogOpen(true);
    try {
      const token = await getAccessToken();
      const data = isPartyVerification
        ? await getAdhocOrderJson(transactionId, fileId, token)
        : await getIdvDetailedJson(fileId, transactionId, token);
      setJsonData(data);
    } catch (err) {
      setJsonError('Failed to load detailed JSON data. Please try again.');
    } finally {
      setJsonLoading(false);
    }
  }, [fileId, transactionId, getAccessToken, isPartyVerification]);

  const handleCloseJsonDialog = useCallback(() => {
    setJsonDialogOpen(false);
    setJsonData(null);
    setJsonError(null);
  }, []);

  const handleButtonClick = useCallback(() => {
    executeIntelliCheckReport().catch((error) => {
      console.error('Failed to generate IntelliCheck report:', error);
    });
  }, [executeIntelliCheckReport]);

  const isReportButtonDisabled = useMemo(
    () => intelliCheckReportStatus === INTELLICHECK_STATUS.PENDING || !transactionId,
    [intelliCheckReportStatus, transactionId],
  );

  const isReportButtonLoading = useMemo(
    () => intelliCheckReportStatus === INTELLICHECK_STATUS.PENDING,
    [intelliCheckReportStatus],
  );

  if (showRequestedDataOnly) {
    return <Box>{requestInfoContainer}</Box>;
  }

  return (
    <Box>
      {requestInfoContainer}
      <Typography variant="h4" className={`${classes.heading} ${isPdf ? classes.pdfHeader : ''}`}>
        Summary
      </Typography>

      <Box className={isPdf ? classes.container : ''}>
        {statuses.map((item, idx) => (
          <StatusRow
            key={`${item.label}-${idx}`}
            index={idx}
            label={item.label}
            value={item.value}
            isPlainText={item.noStatus}
            tooltipText={item?.tooltipText}
            moreInfoTestId={item?.tooltipText ? moreInfoTestId : undefined}
            isPdf={isPdf}
          />
        ))}
      </Box>
      <OrderInsightAlert errorMessages={risks} isPdf={isPdf} hasErrorMessagesHtml />
      {isInteractive && (
        <Box className={classes.container}>
          {!isReportAvailable && (
            <OrderInsightAlert alertWithContent={[{ content: oldVerificationRequestText, severity: 'info' }]} />
          )}
          {isReportAvailable && !isDrawer && (
            <Box>
              <Typography
                className={classes.buttonSpacing}
                variant="body1"
                dangerouslySetInnerHTML={{ __html: identityReportMessage }}
              />
              <Box display="flex" style={{ gap: 8 }}>
                <AgentNetButton
                  variant="outlined"
                  color="primary"
                  data-testid={`${qaPrefix}-button-identity-verification-report${isDrawer ? '' : `-${index}`}`}
                  onClick={handleButtonClick}
                  startIcon={<PDFIcon />}
                  loading={isReportButtonLoading}
                  disabled={isReportButtonDisabled}
                >
                  Identity Verification Report
                </AgentNetButton>
                {showDetailedJson && (
                  <AgentNetButton
                    variant="outlined"
                    color="primary"
                    data-testid={`${qaPrefix}-button-json-response${isDrawer ? '' : `-${index}`}`}
                    onClick={handleViewDetailedJson}
                    disabled={!transactionId}
                  >
                    JSON Response
                  </AgentNetButton>
                )}
              </Box>
            </Box>
          )}
          {isReportAvailable && isDrawer && (
            <OrderInsightAlert
              alertWithContent={[
                {
                  content: (
                    <>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                          View/Download Report
                        </Typography>
                        <AgentNetButton
                          className={classes.reportButton}
                          variant="text"
                          size="small"
                          color="primary"
                          data-testid={drawerIdentityVerificationReportTestId}
                          onClick={handleButtonClick}
                          startIcon={<FileDownloadOutlineIcon />}
                          loading={isReportButtonLoading}
                          disabled={isReportButtonDisabled}
                        >
                          Identity Verification Report
                        </AgentNetButton>
                      </Box>
                      <Typography variant="body2" dangerouslySetInnerHTML={{ __html: identityReportMessage }} />
                    </>
                  ),
                  severity: 'info',
                },
              ]}
            />
          )}
        </Box>
      )}
      {showDetailedJson && (
        <IdvDetailedJsonDialog
          open={jsonDialogOpen}
          onClose={handleCloseJsonDialog}
          jsonData={jsonData}
          loading={jsonLoading}
          error={jsonError}
          customerName={customerName}
        />
      )}
    </Box>
  );
};
export default VerificationResults;
